/** Loading the content bundle in a browser.
 *
 * `@game/content`'s own `loadContent()` reads the data tree with `node:fs` and
 * validates it with zod. Neither belongs in a browser bundle: the bundle was
 * already validated at build time (I8), and the runtime's job is only to index
 * it. So the fetch and the indexing live here, in the app, and the shapes come
 * from the kernel's own contract rather than from a second schema.
 */

import type {
  ArtifactDef, ArtifactDefId, BuildingDef, BuildingDefId, ContentRegistry, CreatureDef,
  CreatureDefId, FactionDef, FactionId, HeroDef, HeroDefId, LevelCurve, MapObjectDef,
  ResourceKind, SecondarySkillDef, SkillDefId, SpellDef, SpellDefId, MagicGroundDef, TerrainDef, TerrainKind,
} from "@game/core";
import { asset } from "../base.js";

/** The wire shape of `packages/content/dist/content.bundle.json`. */
interface ContentBundleWire {
  readonly contentVersion: string;
  readonly creatures: readonly CreatureDef[];
  readonly spells: readonly SpellDef[];
  readonly artifacts: readonly ArtifactDef[];
  readonly skills: readonly SecondarySkillDef[];
  readonly buildings: readonly BuildingDef[];
  readonly heroes: readonly HeroDef[];
  readonly factions: readonly FactionDef[];
  readonly mapObjects: readonly MapObjectDef[];
  readonly terrain: readonly TerrainDef[];
  readonly magicGrounds?: readonly MagicGroundDef[];
  readonly levels: LevelCurve;
  readonly resourceValue: Readonly<Record<ResourceKind, number>>;
  readonly stats: Readonly<Record<string, number>>;
}

function byId<K extends string, V extends { readonly id: K }>(
  rows: readonly V[],
): ReadonlyMap<K, V> {
  const m = new Map<K, V>();
  for (const row of rows) m.set(row.id, row);
  return m;
}

export function registryFromWire(b: ContentBundleWire): ContentRegistry {
  return {
    contentVersion: b.contentVersion,
    creatures: byId<CreatureDefId, CreatureDef>(b.creatures),
    spells: byId<SpellDefId, SpellDef>(b.spells),
    artifacts: byId<ArtifactDefId, ArtifactDef>(b.artifacts),
    skills: byId<SkillDefId, SecondarySkillDef>(b.skills),
    buildings: byId<BuildingDefId, BuildingDef>(b.buildings),
    heroes: byId<HeroDefId, HeroDef>(b.heroes),
    factions: byId<FactionId, FactionDef>(b.factions),
    mapObjects: byId<string, MapObjectDef>(b.mapObjects as readonly (MapObjectDef & { id: string })[]),
    terrain: byId<TerrainKind, TerrainDef>(b.terrain),
    // Absent in a bundle built before Section 31; an empty table is "no magic
    // ground on this map", which is what such a bundle means.
    magicGrounds: byId<string, MagicGroundDef>(b.magicGrounds ?? []),
    levels: b.levels,
    resourceValue: b.resourceValue,
    stats: b.stats,
  };
}

let pending: Promise<ContentRegistry> | null = null;

/** Fetched once per page; every screen shares the one registry. */
export function loadContentBundle(url = asset("/content.bundle.json")): Promise<ContentRegistry> {
  if (pending !== null) return pending;
  pending = (async (): Promise<ContentRegistry> => {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`content bundle: HTTP ${String(res.status)} for ${url}`);
    return registryFromWire((await res.json()) as ContentBundleWire);
  })();
  return pending;
}
